import { matchPath } from 'react-router-dom';

import { ROUTES } from '@/configs/routes';
import { DocumentSectionSlug, TopLevelSectionSlug } from '@/entities/OrganizationSection';

const DESKTOP_TOP_LEVEL_ORDER: TopLevelSectionSlug[] = ['documents', 'news', 'notebook', 'macro', 'chat'];
const MOBILE_TOP_LEVEL_ORDER: TopLevelSectionSlug[] = ['chat', 'documents', 'news', 'notebook', 'macro'];
const DOCUMENT_PREVIEW_SECTION: DocumentSectionSlug = 'document-preview';
const AI_SUMMARY_SECTION: DocumentSectionSlug = 'ai-summary';
const ANALYZE_SECTION: DocumentSectionSlug = 'analyze';
const DOCUMENT_SECTION_ORDER: DocumentSectionSlug[] = [AI_SUMMARY_SECTION, DOCUMENT_PREVIEW_SECTION, ANALYZE_SECTION];

export const getTopLevelSectionRoute = (sectionSlug: TopLevelSectionSlug, organizationId: string | number): string => {
  const normalizedOrganizationId = String(organizationId);

  switch (sectionSlug) {
    case 'documents':
      return ROUTES.organization.documents.create(normalizedOrganizationId);
    case 'news':
      return ROUTES.organization.news.create(normalizedOrganizationId);
    case 'notebook':
      return ROUTES.organization.notebook.create(normalizedOrganizationId);
    case 'macro':
      return ROUTES.organization.macro.create(normalizedOrganizationId);
    case 'chat':
      return ROUTES.organization.documents.chat.create(normalizedOrganizationId);
    default:
      return ROUTES.organization.documents.create(normalizedOrganizationId);
  }
};

export const getFirstAvailableTopLevelSectionRoute = ({
  enabledSections,
  organizationId,
  isPhone,
}: {
  enabledSections: TopLevelSectionSlug[];
  organizationId: string | number;
  isPhone: boolean;
}): string | null => {
  const preferredOrder = isPhone ? MOBILE_TOP_LEVEL_ORDER : DESKTOP_TOP_LEVEL_ORDER;

  for (const sectionSlug of preferredOrder) {
    if (enabledSections.includes(sectionSlug)) {
      return getTopLevelSectionRoute(sectionSlug, organizationId);
    }
  }

  return null;
};

export const getCurrentTopLevelSection = (pathname: string): TopLevelSectionSlug | null => {
  if (matchPath(ROUTES.organization.documents.chat.path, pathname)) {
    return 'chat';
  }

  if (matchPath({ path: ROUTES.organization.documents.path, end: false }, pathname)) {
    return 'documents';
  }

  if (matchPath({ path: ROUTES.organization.news.path, end: false }, pathname)) {
    return 'news';
  }

  if (matchPath({ path: ROUTES.organization.notebook.path, end: false }, pathname)) {
    return 'notebook';
  }

  if (matchPath({ path: ROUTES.organization.macro.path, end: false }, pathname)) {
    return 'macro';
  }

  return null;
};

export const getDocumentSectionRoute = ({
  sectionSlug,
  organizationId,
  documentId,
}: {
  sectionSlug: DocumentSectionSlug;
  organizationId: string | number;
  documentId: string;
}): string => {
  const normalizedOrganizationId = String(organizationId);

  switch (sectionSlug) {
    case 'document-preview':
      return ROUTES.organization.documents.document.documentPreview.create(normalizedOrganizationId, documentId);
    case 'ai-summary':
      return ROUTES.organization.documents.document.aiSummary.create(normalizedOrganizationId, documentId);
    case 'analyze':
      return ROUTES.organization.documents.document.analyze.create(normalizedOrganizationId, documentId);
    default:
      return ROUTES.organization.documents.document.documentPreview.create(normalizedOrganizationId, documentId);
  }
};

export const getFirstAvailableDocumentSectionRoute = ({
  enabledSections,
  organizationId,
  documentId,
}: {
  enabledSections: DocumentSectionSlug[];
  organizationId: string | number;
  documentId: string;
}): string | null => {
  for (const sectionSlug of DOCUMENT_SECTION_ORDER) {
    if (enabledSections.includes(sectionSlug)) {
      return getDocumentSectionRoute({ sectionSlug, organizationId, documentId });
    }
  }

  return null;
};

export const getCurrentDocumentSection = (pathname: string): DocumentSectionSlug | null => {
  if (matchPath({ path: ROUTES.organization.documents.document.documentPreview.path, end: false }, pathname)) {
    return DOCUMENT_PREVIEW_SECTION;
  }

  if (matchPath({ path: ROUTES.organization.documents.document.aiSummary.path, end: false }, pathname)) {
    return AI_SUMMARY_SECTION;
  }

  if (matchPath({ path: ROUTES.organization.documents.document.analyze.path, end: false }, pathname)) {
    return ANALYZE_SECTION;
  }

  return null;
};
